// All endpoints go through Laravel via Nginx /api/v2/ → Laravel /api/
export const BASE = '/api/v2'
// Auth endpoints also on Laravel
export const LARAVEL_BASE = '/api/v2'

export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options?.headers,
    },
    ...options,
  })

  if (res.status === 401) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    throw new ApiError('Not authenticated', 401)
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new ApiError(body.error || `HTTP ${res.status}`, res.status)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),

  upload: async (file: File, options?: { filter?: boolean; filterType?: string | string[]; antiFingerprint?: boolean }) => {
    const fd = new FormData()
    fd.append('image', file)
    const params = new URLSearchParams()
    if (options?.filterType) {
      const ft = Array.isArray(options.filterType) ? options.filterType.filter(f => f !== 'none').join(',') : options.filterType
      if (ft && ft !== 'none') params.set('filterType', ft)
      else params.set('filter', 'false')
    } else if (options?.filter === false) params.set('filter', 'false')
    if (options?.antiFingerprint) params.set('antiFingerprint', 'true')
    const qs = params.toString() ? `?${params}` : ''
    const res = await fetch(`${BASE}/upload${qs}`, { method: 'POST', body: fd, headers: { Accept: 'application/json', ...getAuthHeaders() } })
    if (!res.ok) throw new ApiError('Upload failed', res.status)
    return res.json() as Promise<{ ok: boolean; url: string; type?: 'image' | 'video' }>
  },

  uploadMultiple: async <T = unknown>(files: File[], endpoint: string): Promise<T> => {
    const fd = new FormData()
    files.forEach(f => fd.append('images[]', f))
    const res = await fetch(`${BASE}${endpoint}`, { method: 'POST', body: fd, headers: { Accept: 'application/json', ...getAuthHeaders() } })
    if (res.status === 401) {
      if (window.location.pathname !== '/login') window.location.href = '/login'
      throw new ApiError('Not authenticated', 401)
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'Upload failed' }))
      throw new ApiError(body.error || `HTTP ${res.status}`, res.status)
    }
    return res.json()
  },

  /** Upload a single file with progress tracking */
  uploadSingleWithProgress: <T = unknown>(
    file: File,
    endpoint: string,
    onProgress?: (percent: number) => void,
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const fd = new FormData()
      fd.append('images[]', file)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${BASE}${endpoint}`)
      xhr.setRequestHeader('Accept', 'application/json')
      const authH = getAuthHeaders()
      if (authH.Authorization) xhr.setRequestHeader('Authorization', authH.Authorization)

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }

      xhr.onload = () => {
        if (xhr.status === 401) {
          if (window.location.pathname !== '/login') window.location.href = '/login'
          reject(new ApiError('Not authenticated', 401))
          return
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          let msg = `Erro no upload (HTTP ${xhr.status})`
          try {
            const body = JSON.parse(xhr.responseText)
            if (body.error) msg = body.error
            else if (body.message) msg = body.message
            else if (body.errors) {
              const firstError = Object.values(body.errors).flat()[0]
              if (typeof firstError === 'string') msg = firstError
            }
          } catch {}
          if (xhr.status === 413) msg = 'Arquivo muito grande. Limite do servidor excedido.'
          if (xhr.status === 422) msg = `Validacao: ${msg}`
          reject(new ApiError(msg, xhr.status))
          return
        }
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new ApiError('Resposta invalida do servidor', xhr.status))
        }
      }

      xhr.onerror = () => reject(new ApiError('Erro de rede — verifique o tamanho do arquivo ou sua conexao', 0))
      xhr.ontimeout = () => reject(new ApiError('Upload demorou demais — tente arquivos menores', 0))
      xhr.timeout = 2700000 // 45 min per file
      xhr.send(fd)
    })
  },

  /** Upload multiple files sequentially (one at a time) to avoid server OOM */
  uploadMultipleWithProgress: <T = unknown>(
    files: File[],
    endpoint: string,
    onProgress?: (percent: number) => void,
  ): Promise<T> => {
    return (async () => {
      const total = files.length
      const allImages: unknown[] = []
      const failed: string[] = []
      let totalAdded = 0

      for (let i = 0; i < total; i++) {
        const file = files[i]
        try {
          const result = await api.uploadSingleWithProgress<{ ok: boolean; added: number; images: unknown[]; failed?: string[]; failedCount?: number }>(
            file,
            endpoint,
            (filePercent) => {
              if (onProgress) {
                const overall = Math.round(((i * 100) + filePercent) / total)
                onProgress(Math.min(overall, 99))
              }
            },
          )
          totalAdded += result.added || 0
          if (result.images) allImages.push(...result.images)
          if (result.failed) failed.push(...result.failed)
        } catch (e) {
          failed.push(`${file.name}: ${e instanceof Error ? e.message : 'Erro desconhecido'}`)
        }
      }

      if (onProgress) onProgress(100)

      const response: Record<string, unknown> = {
        ok: totalAdded > 0,
        added: totalAdded,
        images: allImages,
      }
      if (failed.length > 0) {
        response.failed = failed
        response.failedCount = failed.length
      }
      return response as T
    })()
  },
}
